import { useState } from 'react';
import {
  Autocomplete,
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CloseRounded from '@mui/icons-material/CloseRounded';
import { useNavigate } from 'react-router';
import { useAsync } from '@/hooks/useAsync';
import { buscaAlunosAdmin, type AlunoAdmin } from './service';
import { nomeProprio } from './texto';
import { rotuloSemestre } from './types';

const semAcento = (s: string) => s.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();

/**
 * Professor escolhe qualquer aluno da lista do SIGA e abre o painel exatamente como ele veria,
 * tenha ele entrado no sistema ou não.
 */
export function VerComoAlunoDialog({
  aberto,
  onFechar,
}: {
  aberto: boolean;
  onFechar: () => void;
}) {
  const navigate = useNavigate();
  const [busca, setBusca] = useState('');
  const { data, loading } = useAsync(
    (s) => (aberto ? buscaAlunosAdmin('todos', s) : Promise.resolve([] as AlunoAdmin[])),
    [aberto],
  );

  return (
    <Dialog open={aberto} onClose={onFechar} fullWidth maxWidth="sm">
      <DialogTitle sx={{ pr: 6 }}>
        Ver como aluno
        <IconButton
          aria-label="Fechar"
          onClick={onFechar}
          sx={{ position: 'absolute', right: 12, top: 12 }}
        >
          <CloseRounded />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Escolha um aluno para abrir o painel dele exatamente como ele vê, mesmo que ainda não
          tenha entrado no sistema.
        </Typography>
        <Autocomplete
          autoFocus
          openOnFocus
          loading={loading}
          options={data ?? []}
          inputValue={busca}
          onInputChange={(_, v) => setBusca(v)}
          getOptionLabel={(a) => `${nomeProprio(a.nome)} · RA ${a.ra}`}
          filterOptions={(opts, { inputValue }) => {
            const q = semAcento(inputValue.trim());
            return q ? opts.filter((a) => semAcento(a.nome).includes(q) || a.ra.includes(q)) : opts;
          }}
          onChange={(_, a) => {
            if (!a) return;
            onFechar();
            void navigate(`/admin/aluno/${a.ra}`);
          }}
          renderOption={({ key, ...props }, a) => (
            <Box component="li" key={key} {...props}>
              <Stack sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {nomeProprio(a.nome)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  RA {a.ra} ·{' '}
                  {[
                    ...new Set(
                      a.matriculas.map((m) => `${m.disciplina} ${rotuloSemestre(m.semestre)}`),
                    ),
                  ].join(', ')}
                  {a.vinculado_em ? ' · já entrou' : ' · ainda não entrou'}
                </Typography>
              </Stack>
            </Box>
          )}
          renderInput={(params) => (
            <TextField {...params} label="Nome ou RA" placeholder="Comece a digitar…" />
          )}
          noOptionsText="Nenhum aluno com esse nome ou RA"
          loadingText="Carregando alunos…"
        />
      </DialogContent>
    </Dialog>
  );
}
